import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { Router } from '@angular/router';
import {
  deliveryChannelLabelKey,
  deliveryReasonLabelKey,
  deliveryStatusLabelKey,
  deliveryStatusSeverity,
  TagSeverity
} from '../notification-diagnostics/notification-labels';
import { NotificationDeliveryAuditEntry } from '../notification-diagnostics/graphql';

/**
 * Eine Zeile der Zustellungs-Diagnose (#737).
 *
 * Reine Praesentation: haelt keinen Zustand, stellt keine Query. Wird auf beiden Screens verwendet —
 * in der Liste verlinkt, im Detail (dort steht die Notification bereits offen) mit
 * [linkToDetail]="false".
 *
 * Muster nach notification-type-row (#735): Doppelform-Selector, OnPush, Signal-inputs,
 * host-Klassen. Bewusst NICHT standalone — siehe .github/copilot-instructions.md.
 */
@Component({
  selector: 'div[notification-delivery-row], app-notification-delivery-row',
  templateUrl: './notification-delivery-row.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    'class': 'block bg-white border-b border-gray'
  }
})
export class NotificationDeliveryRowComponent {

  private readonly router = inject(Router);

  readonly entry = input.required<NotificationDeliveryAuditEntry>();

  /**
   * Aufgeloester Empfaengername. Die Audit-Zeile traegt nur die UUID; die Liste loest sie ueber
   * dieselbe Nutzerliste auf, die auch den Empfaengerfilter speist. Ist sie (noch) nicht geladen,
   * bleibt die UUID stehen — fuer Support brauchbar, kein Grund die Zeile zurueckzuhalten.
   */
  readonly recipientName = input<string | null>(null);

  readonly linkToDetail = input(true);

  /**
   * Typ und Empfaenger sind Eigenschaften der ELTERN-Notification, nicht der Zustellung. In der
   * flachen Liste unterscheiden sie die Zeilen und muessen stehen; unter ihrer Notification im
   * Detail sind sie fuer alle Kinder gleich und stehen bereits in den Kopfdaten.
   *
   * Bewusst getrennt von linkToDetail, obwohl beide im Detail auf false stehen: sie beantworten
   * verschiedene Fragen, und eine Fahne, die zwei Dinge bedeutet, ist am naechsten Verwendungsort
   * falsch.
   */
  readonly showParentFields = input(true);

  /**
   * Der Drilldown geht ueber notificationId, NICHT ueber id. `id` ist die Delivery-ID;
   * getNotificationAdminDetail mit ihr liefert immer null — der Fehler faellt sonst nur als leerer
   * Detail-Screen auf.
   */
  readonly detailLink = computed<string[]>(() => [
    '/settings/notifications/notification',
    this.entry().notificationId
  ]);

  readonly statusLabelKey = computed(() => deliveryStatusLabelKey(this.entry().status));
  readonly statusSeverity = computed<TagSeverity>(() => deliveryStatusSeverity(this.entry().status));
  readonly reasonLabelKey = computed(() => deliveryReasonLabelKey(this.entry().decisionReason));
  readonly channelLabelKey = computed(() => deliveryChannelLabelKey(this.entry().channel));

  readonly recipientDisplay = computed(() => this.recipientName() || this.entry().recipientUserId);

  /** notBefore interessiert nur, solange nichts gesendet wurde — sonst ist sentAt die Wahrheit. */
  readonly showNotBefore = computed(() => !this.entry().sentAt && !!this.entry().notBefore);

  onRowClick(event: MouseEvent): void {
    if (!this.linkToDetail()) {
      return;
    }
    const link = this.detailLink();
    // Support arbeitet faellenweise und oeffnet gern mehrere Zeilen nebeneinander.
    if (event.metaKey || event.ctrlKey) {
      event.preventDefault();
      window.open(this.router.serializeUrl(this.router.createUrlTree(link)), '_blank');
      return;
    }
    this.router.navigate(link);
  }
}
